import fm from 'format-message';
import isObject from '@grebban/utils/object/isObject';
import errorMessages, { formatInvalidZipWithExample } from '@activebrands/core-web/utils/auth/error-messages';
import { getPostalCodeFormatExample } from '@activebrands/core-web/utils/create-regExp';

type ErrorKey = keyof typeof errorMessages;

const normalizeFieldName = (fieldName: string): string => {
    const baseName = fieldName.includes('_') ? fieldName.slice(fieldName.lastIndexOf('_') + 1) : fieldName;

    return baseName.toLowerCase();
};

const valueMissingErrorKeyByField: Partial<Record<string, ErrorKey>> = {
    address: 'invalidAddress',
    address1: 'invalidAddress',
    addressline1: 'invalidAddress',
    city: 'invalidCity',
    email: 'typeMismatch',
    firstname: 'invalidName',
    lastname: 'invalidName',
    phone: 'invalidNumber',
    phonenumber: 'invalidNumber',
    postalcode: 'invalidZip',
    zipcode: 'invalidZip',
};

const valueMissingMessageByField: Record<string, string> = {
    password: fm('Please enter your password.'),
    passwordconfirmation: fm('Please confirm your password.'),
};

type ValidationErrorObject = {
    error?: string;
    message?: string;
    min?: string | number;
    max?: string | number;
    zipCountry?: string;
    [key: string]: unknown;
};

type ValidationError = string | ValidationErrorObject;

const invalidZipMessage = (error: ValidationErrorObject): string => {
    const country =
        typeof error.zipCountry === 'string' && error.zipCountry.trim() !== '' ? error.zipCountry : undefined;
    const example = getPostalCodeFormatExample(country);
    if (example) {
        return formatInvalidZipWithExample(example);
    }
    return errorMessages.invalidZip;
};

const rangeOverflowMessage = (error?: ValidationErrorObject) =>
    error != null && error.max != null
        ? fm('Please enter a number no greater than { max }.', { max: error.max })
        : fm('Please enter a number that is not above the allowed maximum.');

const rangeUnderflowMessage = (error?: ValidationErrorObject) =>
    error != null && error.min != null
        ? fm('Please enter a number no less than { min }.', { min: error.min })
        : fm('Please enter a number that is not below the allowed minimum.');

const getValidationErrorMessage = (error: ValidationError): string => {
    if (typeof error === 'string') {
        if (error === 'rangeOverflow') {
            return rangeOverflowMessage();
        }
        if (error === 'rangeUnderflow') {
            return rangeUnderflowMessage();
        }
        if (error in errorMessages) {
            return errorMessages[error as ErrorKey];
        }
        return error;
    }
    if (!isObject(error)) {
        return String(error);
    }
    if (error.message && typeof error.message === 'string') {
        return error.message;
    }
    const key = error.error;
    if (key === 'rangeOverflow') {
        return rangeOverflowMessage(error);
    }
    if (key === 'rangeUnderflow') {
        return rangeUnderflowMessage(error);
    }
    if (key === 'invalidZip') {
        return invalidZipMessage(error);
    }
    if (key === 'valueMissing' && typeof error.name === 'string') {
        const field = normalizeFieldName(error.name);
        const errorKey = valueMissingErrorKeyByField[field];

        if (errorKey) {
            return errorMessages[errorKey];
        }

        const fieldMessage = valueMissingMessageByField[field];

        if (fieldMessage) {
            return fieldMessage;
        }
    }
    if (key && key in errorMessages) {
        return errorMessages[key as ErrorKey];
    }
    const values = Object.values(error).filter(v => v != null && v !== key);
    if (values.length) {
        return values.map(String).join(', ');
    }
    return key || '';
};

export default getValidationErrorMessage;
